/*
 * UPCOMING (direction §10).
 *
 * The planning surface: what are my fencers registered for, and when does
 * registration close. The opposite temperament to Active — nothing here is
 * perishable, so hours-old data is not stale and must not be dressed as though it
 * were.
 *
 * THE PLANNING CALIBRATION IS THE RULE MOST EASILY BROKEN HERE. §10: "Quiet
 * 'checked N ago' footer per section — reassurance voice; hours are normal. No
 * amber, no fading, in this regime." A registration list read this morning is
 * fine. Ambering it would train the user to ignore the one signal that matters at
 * a venue, which is the whole reason `AsOf` takes the regime as a parameter rather
 * than computing one policy.
 *
 * BOTH GROUPINGS FROM ONE PAYLOAD. The toggle is a client-side regroup (see
 * lib/regroup.ts), not a second request: it is the same data seen along the other
 * axis, and a second endpoint would make them capable of disagreeing.
 *
 * WHAT IS NOT BUILT, and must not be faked (§14, contract §9):
 *   - The "Registration closing" digest panel. It needs expectation data that
 *     does not exist.
 *   - "Not registered" rows, for the same reason: knowing a fencer is ABSENT from
 *     a roster requires knowing they were expected.
 *   - The "Register" pill. There is no registration flow to link into.
 * Each is absent rather than stubbed. A greyed-out Register button would claim a
 * capability the product does not have.
 */

import { Fragment, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { AsOf } from "../components/AsOf";
import { Band, EventSubHeader } from "../components/Band";
import { CapabilityPill, CapabilityPills } from "../components/CapabilityPill";
import { Card, CardRow } from "../components/Card";
import { HonestState } from "../components/HonestState";
import { SegmentedToggle, type GroupBy } from "../components/SegmentedToggle";
import { UpcomingFencerSummary, UpcomingSummary } from "../components/UpcomingSummary";
import type { Schemas } from "../lib/api";
import { formatLocation } from "../lib/country";
import { dateLocale, dateRange, dayName, deadlineDayLabel, firstLast, venueDay, venueTime } from "../lib/format";
import { regroup } from "../lib/regroup";
import { isTeamRow, teamNames } from "../lib/team";
import { TeamLine } from "../components/TeamLine";
import { fencerCounts, tournamentCounts } from "../lib/summary";
import { visibleUpcoming } from "../lib/upcomingState";
import styles from "./Grouped.module.css";

type Payload = Schemas["UpcomingOutputBody"];
type Tournament = Schemas["UpcomingTournamentBody"];
type Event = Schemas["UpcomingEventBody"];
type Entry = Schemas["UpcomingFencerBody"];

/**
 * Fencer-band key (`personKey`'s value) to an element id on the fencer axis
 * (decision 526). Built in ONE place and shared between the anchor placed on
 * the band's own `<section>` and the fencer summary's `href` — two slugify
 * implementations, one in the summary and one here, is how the href and the
 * id come to disagree.
 */
function fencerAnchorIDs(groups: { key: string }[]): Map<string, string> {
  const out = new Map<string, string>();
  const used = new Set<string>();
  for (const g of groups) {
    // `personKey`'s value is `f:<uuid>`, `a:<uuid>` or `n:<name>`, and the
    // last can hold spaces, commas and diacritics — none of which belong in
    // an element id a fragment has to name.
    const base = `fencer-${g.key.replace(/[^A-Za-z0-9_-]+/g, "-")}`;
    let slug = base;
    for (let n = 2; used.has(slug); n++) slug = `${base}-${n}`;
    used.add(slug);
    out.set(g.key, slug);
  }
  return out;
}

export interface UpcomingProps {
  payload: Payload;
  groupBy: GroupBy;
  onGroupByChange: (next: GroupBy) => void;
  /**
   * One tracked fencer (decision 359): the fencer axis carries no information,
   * so the view renders the tournament axis regardless of the REMEMBERED
   * preference — which is deliberately left untouched, so it comes back intact
   * with fencer #2 — and the toggle is not rendered: "by fencer" over one
   * fencer is a control with one outcome.
   */
  singleFencer?: boolean;
  /** The current query, owned by the route so it survives a re-fetch. */
  search: string;
  onSearchChange: (next: string) => void;
  searching?: boolean;
  /**
   * The list-scope control, or null (multi-list build item 3).
   *
   * A SLOT rather than something this screen builds: the control is gated on two
   * conditions the route holds (decision 510) and reads the router to close its
   * panel, and neither belongs in a screen that is otherwise pure over its
   * props. Manage fencers' commit bar is the precedent (decision 326).
   */
  listScope?: ReactNode;
  /**
   * Whether this view's scope is NARROWER than every list the caller holds
   * (decision 521).
   *
   * It keeps the control on an EMPTY page, which is the one thing `chromeless`
   * below must not withhold: a scope naming a list with nothing in it emptied
   * this page, and the control is the only writer of that scope — stored per
   * view on the user row, never in the URL (501) — so dropping it left no way
   * back. 326 still holds where it was written: scope already every list, page
   * empty, no control.
   */
  scopeNarrowed?: boolean;
  locale?: string;
  now?: Date;
}

export function Upcoming({
  payload,
  groupBy: rememberedGroupBy,
  onGroupByChange,
  singleFencer = false,
  search,
  onSearchChange,
  searching = false,
  listScope = null,
  scopeNarrowed = false,
  locale = dateLocale(),
  now,
}: UpcomingProps) {
  const { t } = useTranslation();

  const groupBy: GroupBy = singleFencer ? "tournament" : rememberedGroupBy;

  /*
   * WITHDRAWN REGISTRATIONS ARE KEPT, NOT FILTERED. §5 gives a withdrawal a full
   * lifecycle in Upcoming while the tournament is ahead (a scratch, day-of FTL
   * state, is no longer sent here: decision 775). The server deliberately
   * returns them (view_upcoming.sql) so the view
   * can render the status honestly — a fencer who pulled out is information a
   * coach needs, and silently dropping the row reads as the registration never
   * having existed.
   *
   * WHAT IS FILTERED is what has already been fenced (decision 357): a fencer
   * whose outcome is known has nothing upcoming in that event, an event with
   * nobody left to fence has nothing upcoming at all, and a tournament follows
   * its last event. Active's let-go rule (decision 351) in the same vocabulary,
   * applied before the transpose so both axes agree. Withdrawn rows carry no
   * outcome, so this filter never touches them; the venue-day backstop is what
   * retires those.
   */
  const groups = regroup<Tournament, Event, Entry>(
    visibleUpcoming(payload.tournaments, now ?? new Date()),
  );

  // Built once per render and shared between the fencer summary's hrefs and
  // the band's own id (decision 526) — see fencerAnchorIDs' own comment.
  const fencerAnchors = fencerAnchorIDs(groups.fencer);

  const empty =
    groupBy === "tournament" ? groups.tournament.length === 0 : groups.fencer.length === 0;

  // Empty AND unsearched: nothing here, and not because of anything the user
  // typed. Only then are the search field and the grouping toggle controls over a
  // list that does not exist — a search that HAPPENED to match nothing keeps
  // both, or there is no way back out of the query (decision 326).
  const chromeless = empty && search.trim().length === 0;

  const headToggle =
    chromeless || singleFencer ? null : (
      <SegmentedToggle value={groupBy} onChange={onGroupByChange} />
    );
  /*
   * THE SCOPE CONTROL SURVIVES AN EMPTY PAGE WHEN THE SCOPE IS NARROW (521,
   * Kevin's fix), which is 326's own test applied rather than its default: a
   * control is withheld from an empty page because it acts on a list that is
   * not there, and this one acts on WHICH LISTS, which is exactly what emptied
   * the page. Withholding it was a trap with no exit — narrow the scope to a
   * list holding nothing and the page lost the only control that could widen
   * it again, for as long as that list stayed empty.
   *
   * The group-by toggle above keeps 326 whole, because regrouping nothing is
   * still nothing.
   */
  const headScope = chromeless && !scopeNarrowed ? null : listScope;

  /*
   * ANCHOR IDS, so a notifications alert links to its TOURNAMENT rather than to
   * this screen (stage 3 phase C, §4.1's "a real link to its destination").
   * `lib/alertGroups.alertDestination` builds `/upcoming#tournament-<id>`, and
   * the browser's own fragment scrolling does the rest — no interception, no
   * scrollIntoView, and the URL is shareable and survives a reload (build rule
   * 10).
   *
   * ONCE PER TOURNAMENT, which is the whole reason this is a Set rather than an
   * attribute on every card. On the FENCER axis a tournament appears once per
   * entered fencer, and repeating the id would produce duplicate element ids —
   * at which point the fragment resolves to whichever the browser saw first and
   * the document stops being valid. First appearance wins; the rest render
   * without one.
   */
  const anchored = new Set<string>();
  const anchorID = (tournamentID: string): string | undefined => {
    if (anchored.has(tournamentID)) return undefined;
    anchored.add(tournamentID);
    return `tournament-${tournamentID}`;
  };

  return (
    <div className={styles.screen}>
      <div className={styles.head}>
        <h1 className={styles.h1}>{t("nav.upcoming")}</h1>
        {/*
          THE HEAD'S CONTROLS, and both of them obey decision 326: on an empty
          unsearched screen they are controls over a list that is not there, so
          they go with the search field below on the same condition. The toggle
          is additionally hidden at one tracked fencer (359) — both axes render
          identical content, so it is a control with one outcome.

          The LIST SCOPE is not hidden at one fencer: how many lists are in view
          is a different question from which axis the rows take, and one tracked
          fencer on one of three lists is exactly the reader who wants to widen.

          THE WRAPPER APPEARS ONLY WHEN SOMETHING IS IN IT, so a screen with
          neither control carries the same markup it did before this existed.
        */}
        {headToggle || headScope ? (
          <div className={styles.headControls}>
            {headScope}
            {headToggle}
          </div>
        ) : null}
      </div>

      {/*
        THE SUMMARY, between the heading and the search field: the first thing
        on the page answers "what is coming and when do I need to be there"
        before any one event's start time does.

        IT FOLLOWS THE GROUPING AXIS (decision 526, amending 522's "renders
        identically on both axes"): a control that says it only regroups the
        page below must not silently leave its own summary describing the
        other axis, so a page grouped by fencer gets a summary of fencers.
        `singleFencer` already forces `groupBy` to "tournament", so the fencer
        summary cannot render for a one-fencer account and needs no extra
        condition here.

        Both return null on an empty page, so neither needs a condition here: a
        summary of nothing is chrome describing an absence the empty state
        already names.
      */}
      {groupBy === "tournament" ? (
        <UpcomingSummary tournaments={groups.tournament} locale={locale} />
      ) : (
        <UpcomingFencerSummary fencers={groups.fencer} anchors={fencerAnchors} />
      )}

      {/*
        The search field, the same as Past's in placement, scope and behaviour —
        including the rule that it SURVIVES a query that matched nothing. "No
        registrations at all" and "no registrations matching this" look identical
        on screen and are opposite situations: hide the field in the second and a
        stray keystroke strands the reader on an empty page with no control to
        undo it.
      */}
      {chromeless ? null : (
        <label className={styles.searchField}>
          <span className="tc-sr-only">{t("upcoming.searchLabel")}</span>
          <input
            className={styles.search}
            type="search"
            name="upcoming-search"
            autoComplete="off"
            placeholder={t("upcoming.search")}
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </label>
      )}

      {empty ? (
        // Plainly, like Active's terminal state: nothing registered is a fact
        // about the calendar, not a failure. The searched case says which of the
        // two it is, because "nothing upcoming" would be a falsehood about the
        // calendar when the truth is about the query.
        <p className={styles.empty}>
          {search.trim() ? t("upcoming.noMatches") : t("upcoming.empty")}
        </p>
      ) : null}

      {/* Kept mounted while a new query is in flight, so the list does not blank
          out between keystrokes — the old results staying put reads as filtering,
          which is what it is. */}
      <div aria-busy={searching || undefined}>
      {empty ? null : groupBy === "tournament" ? (
        groups.tournament.map(({ tournament, events }) => (
          <section
            key={tournament.tournament_id}
            id={anchorID(tournament.tournament_id)}
            className={styles.tournamentCard}
          >
            <TournamentBand
              tournament={tournament}
              locale={locale}
              events={events}
              singleFencer={singleFencer}
              {...(now ? { now } : {})}
            />

            {events.map(({ event, fencers }) => {
              /*
                AT ONE TRACKED FENCER THE ROWS COLLAPSE TOO (decision 359): the
                name and club are the same constant on every row of the screen,
                so a row earns its place only by saying something else — a
                status that is not plain "registered". An event where the one
                fencer is simply registered renders as its header alone, which
                is the whole entry: being listed IS being registered (build
                rule 2), and the reader knows who.
              */
              /*
                A PROJECTION LINE IS ALSO SOMETHING THAT VARIES (decisions
                575-577): an event with a projection says something about the
                fencer that no other row does, so under the one-fencer collapse
                its row earns its place exactly as a non-plain status does.
                Without this, a one-fencer account would never see a projected
                tier.
              */
              /*
                AND SO IS A TEAM (decision 696): which team the one fencer is on
                is the fact a team event's row exists to state.
              */
              const rows = singleFencer
                ? fencers.filter(
                    (f) =>
                      f.status !== "registered" ||
                      showsProjectionLine(event, f) ||
                      f.team_name != null,
                  )
                : fencers;
              return (
              <div key={event.event_id} className={styles.eventBlock}>
                {/*
                  ATTACHED to the card below it (decision 326). The tournament is
                  now the plain heading and the event carries the tint, so an event
                  and its fencers read as one object grouped under the tournament
                  rather than as a new topic. Standalone when the collapse left it
                  no card, so it keeps its own bottom corners.
                */}
                <EventSubHeader
                  attached={rows.length > 0}
                  headingLevel={3}
                  name={eventName(
                    event,
                    t("upcoming.flighted"),
                    eventNonDesignated(event, tournament, t("upcoming.nonDesignatedEvent")),
                  )}
                  // End-aligned (decision 364): names read down the left,
                  // start times down the right.
                  metaLayout="end"
                  // A split event's time line wraps inside itself, beside the
                  // name while a piece fits (decision 661).
                  metaWraps={hasSplitTimes(event)}
                  meta={
                <EventWhen
                  event={event}
                  tournament={tournament}
                  locale={locale}
                  {...(now ? { now } : {})}
                />
              }
                />
                {/*
                  ONE SHARED CARD for the whole event (§10): "rows carry no
                  per-fencer data here, so card-per-name would be border noise".
                  The contrast with Active is deliberate — there each fencer has a
                  strip and gets their own card.
                */}
                {rows.length > 0 ? (
                  <Card attached dense>
                    {rows.map((f) => (
                      // A team's entry has no fencer of its own (decision 714).
                      <CardRow key={f.team_id ?? f.fencer_id} fill>
                        <EntryRow entry={f} nameless={singleFencer} event={event} />
                      </CardRow>
                    ))}
                  </Card>
                ) : null}
              </div>
              );
            })}
          </section>
        ))
      ) : (
        groups.fencer.map((group) => (
          <section
            key={group.key}
            id={fencerAnchors.get(group.key)}
            className={styles.tournamentCard}
          >
            {/* Fencer bands: the name, and now the summary — a fencer has no
                dates or location of their own (§10), but "3 tournaments, 7
                events" is a fact about them rather than borrowed chrome. */}
            <Band
              plain
              // DISPLAY flips to First Last; the SORT stayed on the stored
              // surname-first string in regroup.byFencer (decision 358).
              name={firstLast(group.name)}
              headingLevel={2}
              meta={<FencerSummary tournaments={group.tournaments} />}
            />

            {group.tournaments.map(({ tournament, events }) => (
              <div
                key={tournament.tournament_id}
                id={anchorID(tournament.tournament_id)}
                className={styles.eventBlock}
              >
                <EventSubHeader
                  attached
                  headingLevel={3}
                  name={tournament.name}
                  // Stacked (decision 364): this meta is three facts deep, and
                  // beside the name it read as a weird second column.
                  metaLayout="stacked"
                  meta={
                    <>
                      <TournamentMeta tournament={tournament} locale={locale} />
                      {/*
                        ITS OWN ROW, which §10 requires explicitly and which this
                        axis was not honouring: `Band` gives the deadline a
                        `.deadline` block with its own margin, but the fencer axis
                        renders the tournament through EventSubHeader, where both
                        landed inside one `.meta` and ran together with no gap
                        (decision 327).
                      */}
                      <span className={styles.deadlineRow}>
                        <DeadlineRow tournament={tournament} locale={locale} now={now} />
                      </span>
                      <SectionFooter
                        checkedAt={tournament.checked_at}
                        {...(now ? { now } : {})}
                      />
                    </>
                  }
                  action={
                    /*
                      THE CAPABILITY TRAVELS WITH THE TOURNAMENT wherever it
                      appears, repeated per appearance. §10 is explicit that
                      "deduplication would break behavioral consistency" — the same
                      destination must be reachable by the same gesture from every
                      place the tournament is shown.
                    */
                    <TournamentLinks tournament={tournament} />
                  }
                />

                {/* On this axis the rows are the fencer's EVENTS, with day and
                    time in the trailing slot (§10, placement per decision 364):
                    event names read down the left, times down the right, one
                    line per event. */}
                <Card attached dense>
                  {events.map(({ event, entry }) => (
                    <CardRow
                      // A person an organiser entered on two teams has two rows
                      // in one event (decision 714), so the team is in the key.
                      key={`${event.event_id}:${entry.team_id ?? ""}`}
                      // The one row whose trailing slot may shrink: a split
                      // event's time, wrapping at its pieces (decision 661).
                      trailingWraps={hasSplitTimes(event)}
                      trailing={
                        <EventWhen
                          event={event}
                          tournament={tournament}
                          locale={locale}
                          {...(now ? { now } : {})}
                        />
                      }
                    >
                      <div className={styles.rowName}>
                        {eventName(
                          event,
                          t("upcoming.flighted"),
                          eventNonDesignated(event, tournament, t("upcoming.nonDesignatedEvent")),
                        )}
                      </div>
                      <EntryTeam entry={entry} />
                      <EntryStatus entry={entry} />
                      <ProjectionLine event={event} entry={entry} />
                    </CardRow>
                  ))}
                </Card>

              </div>
            ))}
          </section>
        ))
      )}
      </div>
    </div>
  );
}

/**
 * The tournament band (§10): "always rendered" on this axis.
 *
 * Active renders its own always too now — the plural-only rule there was reversed
 * when the three tiers became one bordered card, since a card whose heading
 * appears only alongside a second tournament is a card with no subject. So the
 * two screens no longer differ here.
 */
function TournamentBand({
  tournament,
  locale,
  events,
  singleFencer = false,
  now,
}: {
  tournament: Tournament;
  locale: string;
  events: { event: Event; fencers: Entry[] }[];
  singleFencer?: boolean;
  now?: Date;
}) {
  return (
    <Band
      // PLAIN on this screen (decision 326): the tint moved down to the event.
      plain
      headingLevel={2}
      name={tournament.name}
      meta={<TournamentMeta tournament={tournament} locale={locale} />}
      // IN ITS OWN CELL of the facts grid, never wrapped into the location text
      // (§10's "own full row", knowingly narrowed by decision 363 — the cell
      // keeps the rule's point while a wide band spends one row, not three).
      deadline={<DeadlineRow tournament={tournament} locale={locale} now={now} />}
      /*
        The summary and the freshness line share the third cell: both are quiet
        commentary on the band rather than facts a coach chases, so they group
        (decision 363). The freshness line stays LAST among the facts, keeping
        decision 327's ordering — it moved up from a footer under the last
        event, where it read as a stray sentence belonging to nothing.
      */
      footer={
        <>
          <TournamentSummary events={events} singleFencer={singleFencer} />
          <SectionFooter checkedAt={tournament.checked_at} {...(now ? { now } : {})} />
        </>
      }
      action={<TournamentLinks tournament={tournament} />}
    />
  );
}

/**
 * A tournament's destinations: Details, and on an international tournament
 * its FIE or EFC page beneath it (decision 948). Each renders only with its
 * URL (the capability rule), so a tournament with neither renders nothing.
 */
function TournamentLinks({ tournament }: { tournament: Tournament }) {
  const details = tournament.details_url;
  const intl = tournament.international_url;
  const source = tournament.international_source;
  const international = intl && source ? <CapabilityPill kind={source} href={intl} /> : null;
  if (!details) return international;
  if (!international) return <CapabilityPill kind="details" href={details} />;
  return (
    <CapabilityPills>
      <CapabilityPill kind="details" href={details} />
      {international}
    </CapabilityPills>
  );
}

function TournamentMeta({
  tournament,
  locale,
}: {
  tournament: Tournament;
  locale: string;
}) {
  const { t } = useTranslation();
  const when = dateRange(tournament.start_date, tournament.end_date, locale);
  // City and country abroad, city and region at home (decision 776).
  const where = formatLocation(tournament.city, tournament.region, tournament.country, locale);
  // A non-designated meet says so in its header, on both axes (Kevin,
  // 2026-10-14), as the last fact of the line.
  const non = tournament.non_designated ? t("upcoming.nonDesignated") : "";

  // One line, dot-separated, rather than the old two (decision 363): dates and
  // location are one "when and where" fact, and they wrap naturally when the
  // cell is narrow.
  return (
    <>
      {when ? <span className="tc-num">{when}</span> : null}
      {when && where ? " · " : null}
      {where || null}
      {non && (when || where) ? " · " : null}
      {non || null}
    </>
  );
}

/**
 * The regular-close deadline: the DAY, and only the day (decision 360).
 *
 * The exact time is deliberately not rendered. What is STORED is untouched — a
 * full timestamptz, exact for USA Fencing and end-of-day-in-zone for FRED
 * (decisions 134, 144) — but on screen the minute was precision the value does
 * not really have across sources, and "2:59 AM EDT" made a fee boundary read
 * like a flight departure. deadlineDayLabel prints the day the source MEANT,
 * which for a Pacific-stated deadline at an Eastern venue is not the date of
 * the stored instant; see format.ts for the rule.
 *
 * Null renders nothing — no "no deadline", no "TBA". Many local events genuinely
 * have none, and inventing a row for the absence would make the common case look
 * incomplete.
 *
 * ONCE THE INSTANT HAS PASSED it reads "Regular registration closed", with no day
 * (Kevin's review): "closes Friday" under a tournament whose deadline went by on
 * Friday reads as still open. The stored instant decides, not the displayed day,
 * because the instant is when the source actually stops taking entries.
 */
function DeadlineRow({
  tournament,
  locale,
  now,
}: {
  tournament: Tournament;
  locale: string;
  now?: Date | undefined;
}) {
  const { t } = useTranslation();

  const closes = tournament.registration_closes_at;
  if (!closes) return null;

  if (Date.parse(closes) <= (now ?? new Date()).getTime()) return <>{t("upcoming.closed")}</>;

  const day = deadlineDayLabel(closes, tournament.timezone, locale);
  if (!day) return null;

  return <>{t("upcoming.closes", { day })}</>;
}

/**
 * An event's date and start time (§10): "the event date — weekday + day ('Sat
 * 29'), month added when the tournament spans months — plus venue-local start
 * time (or 'time not published')".
 *
 * THE UNPUBLISHED TIME IS THE ONE PLACE THIS VIEW NAMES AN ABSENCE, and it is
 * named rather than blank because the absence is the answer to a question the user
 * is actively asking. That is not a contradiction of "null renders nothing": the
 * rule bars invented VALUES and placeholder dashes, not an honest sentence about
 * what the source has not said yet.
 *
 * IT READS "no time yet" AND USED TO READ "time not published" — nineteen
 * characters saying very little, at the end of the narrowest line on the screen,
 * where it was a common cause of an awkward wrap. The shorter phrase keeps both
 * halves of the meaning: "no time" is the absence, and "yet" is what stops it
 * reading as a permanent property of the event. "Yet" is a claim the data
 * supports rather than a hopeful one — this view only ever shows events that
 * have not happened, and decision 449 records national events as publishing no
 * time until after the entry deadline, which is precisely the case a reader
 * meets here.
 */
function EventWhen({
  event,
  tournament,
  locale,
  now,
}: {
  event: Event;
  tournament: Tournament;
  locale: string;
  now?: Date;
}) {
  const { t } = useTranslation();

  /*
   * THE MONTH APPEARS ONLY WHEN THE TOURNAMENT SPANS MONTHS, per build rule 7,
   * decided from the TOURNAMENT's span rather than the event's own date —
   * because the rule is about disambiguating within the group being read, and
   * the band above this row has already said "Sat 21 – Sun 22 Nov".
   *
   * THIS WAS ALREADY THE INTENT AND WAS NOT THE BEHAVIOUR. The line read
   * `{day ? (spansMonths ? day : day) : null}` — both arms identical — so
   * `spansMonths` was computed and thrown away and `dateLabel` always included
   * the month. A single-month tournament therefore restated it on every event
   * row, which on a phone is the difference between "Sun 22" and "Sun 22 Nov"
   * on the narrowest line in the app: three characters times every event, on
   * the row that also carries the event name and the time.
   *
   * So this is a correction to build rule 7's own rule rather than a departure
   * from it.
   */
  const spansMonths =
    !!tournament.start_date &&
    !!tournament.end_date &&
    tournament.start_date.slice(0, 7) !== tournament.end_date.slice(0, 7);

  /*
   * "TODAY" AND "TOMORROW" BEFORE A DATE (Kevin, 2026-09-10), for the same
   * reason the month came off: the band above has already stated the span, so
   * the row's job is to place the event WITHIN it, and "Today" does that where
   * "Sun 22" makes the reader do the arithmetic. Anything further out falls
   * back to the date, which cannot go stale.
   *
   * Venue zone, not the device's (build rule 7): a coach in Denver reading
   * about an Eastern tournament at 10pm is looking at a venue where it is
   * already tomorrow.
   */
  /*
   * A SPLIT EVENT BEFORE FTL LISTS ITS TIERS (decision 656) carries the two
   * tier times from USA Fencing's schedule image and no starts_at of its own,
   * so its line reads "Fri 9 · E – 8:00 AM / C – 2:30 PM": soonest first, the
   * letters the image itself prints, and the day of the earliest tier's
   * INSTANT in the venue zone. Every other event takes today's path unchanged,
   * "no time yet" included.
   */
  const tiers = hasSplitTimes(event) ? sortedTierStarts(event.tier_starts) : [];
  const first = tiers[0];
  const named = dayName(
    first ? venueDay(new Date(first.starts_at), tournament.timezone) : event.event_date,
    tournament.timezone,
    locale,
    spansMonths,
    now ?? new Date(),
  );
  const day =
    named == null
      ? null
      : named.kind === "today"
        ? t("date.today")
        : named.kind === "tomorrow"
          ? t("date.tomorrow")
          : named.label;

  if (first) {
    // "(Flighted)" after a tier's time only when the tiers DIFFER; when every
    // tier is flighted the marker goes by the event name instead (eventName).
    const perTier = tiers.some((x) => x.flighted) && !tiers.every((x) => x.flighted);
    return (
      <span className="tc-num">
        {/*
          THE LINE BREAKS ONLY BETWEEN PIECES: after the date and at the "/"
          (Kevin, at review). The plain space between two nowrap pieces is the
          only break opportunity, so a phone gets at most two breaks.
        */}
        {day ? <span className={styles.whenPiece}>{day} ·</span> : null}
        {/*
          THE LETTER LEADS (Kevin, at review): "E – 8:00 AM EDT", not "8:00 AM
          EDT E". With the zone shown, a trailing letter ran into "AM EDT" as one
          string of capitals, all but the last about time. Leading, the letter
          reads as a label and "(Flighted)" stays beside the time it qualifies.
        */}
        {tiers.map((tier, i) => (
          <Fragment key={tier.tier}>
            {day || i > 0 ? " " : null}
            <span className={styles.whenPiece}>
              {tier.tier === "elite" ? t("upcoming.tierLetter.elite") : t("upcoming.tierLetter.challenger")}
              {" – "}
              {venueTime(tier.starts_at, { timeZone: tournament.timezone, locale })}
              {perTier && tier.flighted ? ` ${t("upcoming.flighted")}` : null}
              {i < tiers.length - 1 ? " /" : null}
            </span>
          </Fragment>
        ))}
      </span>
    );
  }

  const time = venueTime(event.starts_at, {
    timeZone: tournament.timezone,
    locale,
  });

  return (
    <span className="tc-num">
      {day}
      {day && (time || !event.starts_at) ? " · " : null}
      {time ?? <span className={styles.notPublished}>{t("upcoming.timeNotPublished")}</span>}
    </span>
  );
}

type TierStart = Schemas["UpcomingTierStartBody"];

/**
 * Whether EventWhen renders the split branch (decision 656), which is the one
 * line whose CONTAINERS may wrap it (decision 661). One test for both, so a
 * container can never opt into wrapping a line that is not the split one.
 */
function hasSplitTimes(event: Event): boolean {
  return (event.tier_starts?.length ?? 0) > 0;
}

/** Soonest first, Elite first on a tie (decision 656). Absent is none. */
function sortedTierStarts(tiers: readonly TierStart[] | null | undefined): TierStart[] {
  return [...(tiers ?? [])].sort(
    (a, b) =>
      new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime() ||
      (a.tier === b.tier ? 0 : a.tier === "elite" ? -1 : 1),
  );
}

/**
 * The event's name, with "(Flighted)" when the WHOLE event runs its pools in two
 * waves (decision 656): an unsplit event the schedule image marks, or a split
 * one whose every tier it marks. When only one tier is flighted the marker is on
 * that tier's time instead, in EventWhen.
 */
function eventName(event: Event, flighted: string, nonDesignated: string | null): string {
  const tiers = event.tier_starts ?? [];
  const whole = tiers.length > 0 ? tiers.every((x) => x.flighted) : event.flighted === true;
  const name = whole ? `${event.event_name} ${flighted}` : event.event_name;
  return nonDesignated ? `${name} ${nonDesignated}` : name;
}

/**
 * "(Non-designated)" for an event whose tournament header does not already say
 * it (Kevin, 2026-10-14): in a meet that mixes designated and non-designated
 * events the meet is not labelled, so the event is. Null otherwise.
 */
function eventNonDesignated(event: Event, tournament: Tournament, label: string): string | null {
  return event.non_designated && !tournament.non_designated ? label : null;
}

/**
 * A registered fencer: name, and a status only when it is not the norm.
 *
 * NO CLUB (decision 362): the reader already knows the club of a fencer they
 * chose to track, so the line restated a constant. The wire still carries it;
 * the identity surfaces still render it.
 *
 * `nameless` is the one-tracked-fencer collapse (decision 359): the name is
 * the same constant on every row of the screen, so the row keeps only what
 * varies — the status. The row itself only renders at all when that status is
 * worth a line, which the caller decides.
 */
function EntryRow({
  entry,
  event,
  nameless = false,
}: {
  entry: Entry;
  event: Event;
  nameless?: boolean;
}) {
  // The name and the projection share one line (Kevin, 2026-09-22): the
  // projection sits beside the name, left-aligned, and the name stays centred
  // against it when the projection wraps to a second line on a narrow screen.
  // A team's entry names its tracked members, as Active's card does (decision
  // 714); see lib/team.ts.
  const { t } = useTranslation();
  const team = isTeamRow(entry) ? teamNames(entry.team_members) : null;
  const name =
    team && team.tracked.length > 0 ? team.tracked.join(t("team.nameSeparator")) : firstLast(entry.name);
  return (
    <>
      <div className={styles.nameAndSeed}>
        {nameless ? null : <div className={styles.rowName}>{name}</div>}
        <ProjectionLine event={event} entry={entry} inline />
      </div>
      <EntryTeam entry={entry} />
      <EntryStatus entry={entry} />
    </>
  );
}

/**
 * The team a team-event entry is on (decisions 696, 714), with the teammates
 * nobody here tracks; nothing otherwise. The name alone, with no "Team" label:
 * the event already says it is a team event.
 */
function EntryTeam({ entry }: { entry: Entry }) {
  if (!entry.team_name) return null;
  return <TeamLine teamName={entry.team_name} members={entry.team_members} className={styles.rowMeta} as="span" />;
}

/**
 * The registration status, rendered ONLY when it is not plain "registered".
 *
 * PRESENCE IMPLIES STATE (build rule 2): being listed IS being registered, so a
 * "Registered" badge on every row would be noise on the 95% case and would make
 * the exceptions harder to spot rather than easier.
 *
 * The ones that DO render are the ones a coach must not miss. `withdrawn` gets an
 * honest-state disclosure because §5 gives it a lifecycle and the user needs to
 * know it came from the roster rather than from them. `unknown` is rendered
 * honestly per contract §4 — the source listed the entry without a status, and
 * saying so is better than promoting it to "registered".
 *
 * NO DAY-OF FTL STATE (decision 775): a check-in and a scratch are FTL's
 * readings of the day, and FTL marks every FIE and EFC competitor checked in
 * the evening before (728). The server sends neither on Upcoming, so a
 * checked-in or scratched fencer's row is a plain registered row here, and
 * Active is where the day is read.
 */
function EntryStatus({ entry }: { entry: Entry }) {
  const { t } = useTranslation();

  switch (entry.status) {
    case "withdrawn":
      return (
        <HonestState
          label={t("upcoming.status.withdrawn")}
          detail={t("upcoming.status.withdrawnDetail")}
        />
      );
    case "unknown":
      return (
        <HonestState
          label={t("upcoming.status.unknown")}
          detail={t("upcoming.status.unknownDetail")}
        />
      );
    case "waitlisted":
    case "pending":
      // Rendered on the RIGHT, as the lead piece of the projection line
      // (decisions 596, 660, Kevin) -- not here under the name.
      return null;
    case "registered":
    default:
      return null;
  }
}

/**
 * Whether a row carries a projection line: the event is one the projection
 * applies to, and the entry is still going to be fenced. A withdrawal has no
 * tier to project, and a line saying so would restate the status above it.
 */
function showsProjectionLine(event: Event, entry: Entry): boolean {
  if (entry.status === "withdrawn") return false;
  // A SPLIT EVENT SHOWS THE BADGE INSTEAD (migration 0041), and the badge comes
  // from USA Fencing's entrants payload rather than from the points list -- so it
  // shows with or without a tier_projection, and a fencer in neither tier shows
  // nothing at all rather than a projection the split has already overtaken.
  if (event.tiered) return entry.tier != null;
  return event.tier_projection != null;
}

/**
 * The projected tier (decisions 575-577): "Projected Elite · 14th in Junior ·
 * +132 points from cutoff". Three pieces -- the tier, the ONE path the server
 * chose (the closest to its cutoff) with the fencer's rank on that list, and
 * the gap in points -- and deliberately nothing more. PLAIN TEXT, NO CHIP
 * (Kevin, 2026-09-29): the chip is for a tier USA Fencing has confirmed, and a
 * projection wearing one would read as the badge.
 *
 * EVERY WORD IS WRITTEN HERE (decision 416); the server has already applied
 * the gates that decide WHICH tier is sent (576), so this only picks words. An
 * event the projection does not apply to carries no `tier_projection` and
 * renders nothing at all; an eligible one with nothing to say for this fencer
 * renders "No tier projection", because on an eligible event the absence IS
 * the answer to the question being asked. Points themselves are never shown,
 * only the gap.
 *
 * The line's variants, each a fact the payload carries:
 *   - a path and a gap: all three pieces;
 *   - a path and no gap: "No cutoff" -- the path has none, or the field fits;
 *   - no path, and the field fits the Elite: "Projected Elite · No cutoff";
 *   - no path otherwise: the tier alone (a field below the split).
 *
 * UNBREAKABLE PIECES, so a line too narrow for all of them breaks at a dot and
 * nowhere else. `inline` is the tournament axis, where the pieces sit beside
 * the fencer's name (Kevin, 2026-09-22: the rows stay as short as possible);
 * the fencer axis keeps its own line under the event name.
 *
 * A WAITLISTED ENTRY LEADS THE LINE (decision 596, Kevin): "Waitlisted (#2)" is
 * the first piece, so it sits on the right of the card with whatever the
 * projection has to say after it. On an event the projection does not apply
 * to -- every international one, where USA Fencing's own place in line is the
 * whole answer -- it is the line's only piece.
 *
 * A PENDING ENTRY LEADS IT THE SAME WAY (decision 660): "Pending" takes the
 * waitlist's place in every one of those cases, through the one `statusLead`
 * value, so the two statuses cannot drift apart.
 */
function ProjectionLine({ event, entry, inline = false }: { event: Event; entry: Entry; inline?: boolean }) {
  const { t, i18n } = useTranslation();

  const Tag = inline ? "span" : "div";
  const cls = `tc-num ${styles.rowMeta} ${styles.seedLine}`;

  const statusLead =
    entry.status === "pending"
      ? t("upcoming.status.pending")
      : entry.status !== "waitlisted"
        ? null
        : entry.waitlist_position != null
          ? t("upcoming.status.waitlistedAt", { position: entry.waitlist_position })
          : t("upcoming.status.waitlisted");

  if (!showsProjectionLine(event, entry)) {
    return statusLead == null ? null : (
      <Tag className={cls}>
        <span className={styles.seedPiece}>{statusLead}</span>
      </Tag>
    );
  }

  if (event.tiered) return <TierLine entry={entry} Tag={Tag} cls={cls} inline={inline} lead={statusLead} />;

  const projection = event.tier_projection!;
  const tier = entry.projected_tier;
  const pieces: string[] = statusLead == null ? [] : [statusLead];
  if (projection.unavailable || tier == null) {
    pieces.push(t("upcoming.projection.none"));
    return <PieceLine Tag={Tag} cls={cls} pieces={pieces} />;
  }

  const list = entry.projection_list;
  const position = entry.projection_position;
  const gap = entry.gap_from_cutoff;
  pieces.push(tier === "elite" ? t("upcoming.projection.elite") : t("upcoming.projection.challenger"));
  if (list != null && position != null) {
    // An ordinal through i18next's plurals (decision 485): several locales
    // inflect it to agree with what follows, so the translator owns the phrase.
    pieces.push(
      t("upcoming.projection.rank", {
        count: position,
        ordinal: true,
        list: t(`upcoming.projection.list.${list}`),
      }),
    );
  }
  if (gap != null) {
    pieces.push(
      t("upcoming.projection.fromCutoff", {
        // "points" and not a bare number (Kevin, 2026-09-22): "+12 from cutoff"
        // can read as twelve PLACES. The locale's own signed number, "+231",
        // "-87", and "0" with no sign; a gap of one is "+1 point".
        count: Math.abs(gap),
        points: new Intl.NumberFormat(i18n.language, { signDisplay: "exceptZero" }).format(gap),
      }),
    );
  } else if (list != null || projection.entrant_count <= projection.elite_slots) {
    // A path with no cutoff, or a field that fits the Elite: there is none, and
    // a blank would read as missing data.
    pieces.push(t("upcoming.projection.noCutoff"));
  }
  return <PieceLine Tag={Tag} cls={cls} pieces={pieces} />;
}

/** The projection line's pieces, each unbreakable, joined at a "·". */
function PieceLine({ Tag, cls, pieces }: { Tag: "span" | "div"; cls: string; pieces: string[] }) {
  return (
    <Tag className={cls}>
      {pieces.map((piece, i) => (
        <span key={i} className={styles.seedPiece}>
          {piece}
          {i < pieces.length - 1 ? " ·" : null}
        </span>
      ))}
    </Tag>
  );
}

/**
 * The national split's tier, in the seed line's place (migration 0041): a chip
 * reading "Elite" or "Challenger", and for Challenger its projected place in
 * line -- "Projected 5th in line" -- among the Challenger entrants by points on
 * the event's own list. No seed and no cutoff gap: the badge already says which
 * side of the cut the fencer is on. A Challenger fencer with no known points
 * gets the chip alone.
 *
 * The place goes through i18next's ORDINAL plurals for the reason alertText.ts
 * gives: several locales inflect an ordinal to agree with what follows, so the
 * translator owns the whole phrase.
 *
 * THE CHIP SITS ON THE LINE'S ALIGNED EDGE (Kevin), so every chip in a card
 * lines up whether or not a place follows it: LAST on the tournament axis,
 * where the line is end-aligned beside the name, and FIRST on the fencer axis,
 * where it is start-aligned under the event name. No "·" between the two
 * pieces, unlike the seed line: the chip's border already separates them.
 */
function TierLine({
  entry,
  Tag,
  cls,
  inline,
  lead = null,
}: {
  entry: Entry;
  Tag: "span" | "div";
  cls: string;
  inline: boolean;
  // "Waitlisted (#2)", first on the line on either axis (decision 596).
  lead?: string | null;
}) {
  const { t } = useTranslation();
  const elite = entry.tier === "elite";
  const position = elite ? null : entry.challenger_position;
  const chip = (
    <span className={styles.seedPiece}>
      <span className={elite ? `${styles.tierChip} ${styles.tierElite}` : styles.tierChip}>
        {elite ? t("upcoming.tier.elite") : t("upcoming.tier.challenger")}
      </span>
    </span>
  );
  const place =
    position == null ? null : (
      <span className={styles.seedPiece}>
        {t("upcoming.tier.inLine", { count: position, ordinal: true })}
      </span>
    );
  return (
    <Tag className={`${cls} ${styles.tierLine}`}>
      {lead == null ? null : <span className={styles.seedPiece}>{lead}</span>}
      {inline ? place : chip}
      {inline ? chip : place}
    </Tag>
  );
}

/**
 * §10's "quiet 'checked N ago' footer per section".
 *
 * The PLANNING regime, which is the whole point: reassurance voice, hours are
 * normal, no amber and no fading. Renders nothing when the server sent no
 * timestamp, rather than claiming a freshness it cannot support.
 */
/**
 * The freshness line, in the tournament's own facts rather than under its events.
 *
 * A <span> and not a <p>: it now sits inside a heading's meta stack, and a
 * paragraph nested in that is invalid where the meta is itself inline content.
 * `.freshness` makes it a block, so it still gets its own line.
 *
 * "Checked for updates N ago" rather than "Checked N ago" (decision 327). The
 * value is `max(registrations.last_seen_at)` — when we last LOOKED — so the
 * shorter "Updated N ago" would claim the data changed, which is precisely what
 * this number does not know. The longer phrase costs a few characters in the
 * smallest type on the screen and stops the app asserting something untrue about
 * the one thing it exists to be honest about.
 */
function SectionFooter({ checkedAt, now }: { checkedAt?: string | undefined; now?: Date }) {
  if (!checkedAt) return null;

  return (
    <span className={styles.freshness}>
      <AsOf observedAt={checkedAt} regime="planning" {...(now ? { now } : {})} />
    </span>
  );
}

/*
 * "3 fencers in 7 events" on a tournament band, "3 tournaments, 7 events" on a
 * fencer band (decision 326).
 *
 * WHY IT EARNS ITS LINE. The tiers below a band are collapsed into it: a
 * tournament heading is followed by events, and the question a coach has at that
 * heading is "how much of this concerns me" — which the band could not answer
 * without scrolling through it. It is a count of what is already on screen, not
 * new data, so it costs one line and no request.
 *
 * Composed from pluralised fragments dropped into a translated template, so a
 * locale can reorder the sentence and pluralise each count on its own rules. See
 * lib/summary.ts for the counting, and specifically for the deduplication — the
 * same fencer across six events is one fencer.
 */
function TournamentSummary({
  events,
  singleFencer = false,
}: {
  events: { event: Event; fencers: Entry[] }[];
  singleFencer?: boolean;
}) {
  const { t } = useTranslation();
  const counts = tournamentCounts(events.map(({ fencers }) => ({ fencers })));

  // "1 fencer in 6 events" when the whole account tracks one fencer restates
  // the screen's one constant per band (decision 359); the event count is the
  // part that varies.
  return (
    <span className={styles.summary}>
      {singleFencer
        ? t("summary.eventsOnly", { events: t("summary.events", { count: counts.events }) })
        : t("summary.fencersInEvents", {
            fencers: t("summary.fencers", { count: counts.fencers }),
            events: t("summary.events", { count: counts.events }),
          })}
    </span>
  );
}

function FencerSummary({
  tournaments,
}: {
  tournaments: { events: { event: Event; entry: Entry }[] }[];
}) {
  const { t } = useTranslation();
  const counts = fencerCounts(tournaments);

  return (
    <span className={styles.summary}>
      {t("summary.tournamentsAndEvents", {
        tournaments: t("summary.tournaments", { count: counts.tournaments }),
        events: t("summary.events", { count: counts.events }),
      })}
    </span>
  );
}
